import React, { useState } from 'react';
import { Badge, Tooltip, Progress, Button, Modal, message } from 'antd';
import { ThunderboltOutlined, ExclamationCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAppTheme } from '../../theme/ThemeProvider';
import { useUsageContext } from '../../contexts/UsageContext';

interface UsageIndicatorProps {
  showDetails?: boolean;
  size?: 'small' | 'default' | 'large';
  onUsageExceeded?: () => void;
}

const UsageIndicator: React.FC<UsageIndicatorProps> = ({ 
  showDetails = false, 
  size = 'default',
  onUsageExceeded 
}) => {
  const { t, i18n } = useTranslation();
  const { theme } = useAppTheme();
  const { usageStatus, loading, refreshUsage } = useUsageContext();
  const [modalVisible, setModalVisible] = useState(false);

  // Refresh usage status
  const handleRefresh = () => {
    refreshUsage();
  };

  // Show detailed usage modal
  const showUsageModal = () => {
    setModalVisible(true);
  };

  // If we don't have usage status yet, render nothing
  if (!usageStatus) {
    return null;
  }

  const { remaining, used, limit, canUse } = usageStatus;

  // Backend returns limit: null, remaining: null, can_use: true for unlimited plans
  // (e.g. Enterprise). In the frontend mapping this becomes limit: 0, remaining: 0,
  // so we treat (limit === 0 && canUse) as "unlimited" to avoid misinterpreting it
  // as "quota exhausted".
  const isUnlimited = limit === 0 && canUse;

  // Values used for display (∞ for unlimited)
  const displayRemaining = isUnlimited ? '∞' : remaining;
  const displayLimit = isUnlimited ? '∞' : limit;

  // Safe values for progress calculation (always show 0% for unlimited)
  const safeLimit = isUnlimited ? 1 : limit;
  const safeUsed = isUnlimited ? 0 : used;
  const percentage = safeLimit > 0 ? (safeUsed / safeLimit) * 100 : 0;

  // Determine color based on remaining quota
  const getStatusColor = () => {
    if (isUnlimited) return '#52c41a'; // 绿色 - 无限
    if (remaining === 0) return '#ff4d4f'; // 红色 - 已用完
    if (remaining <= 2) return '#faad14'; // 橙色 - 即将用完
    return '#52c41a'; // 绿色 - 正常
  };

  const getStatusText = () => {
    if (isUnlimited) {
      return i18n.language === 'zh-CN' ? '无限制套餐' : 'Unlimited plan';
    }
    if (remaining === 0) {
      return i18n.language === 'zh-CN' ? '今日次数已用完' : 'Daily limit reached';
    }
    if (remaining <= 2) {
      return i18n.language === 'zh-CN' ? `仅剩 ${remaining} 次` : `Only ${remaining} left`;
    }
    return i18n.language === 'zh-CN' ? `剩余 ${remaining} 次` : `${remaining} remaining`;
  };

  // Compact display (icon + badge only)
  if (size === 'small') {
    return (
      <Tooltip title={getStatusText()}>
        <Badge 
          count={displayRemaining} 
          style={{ 
            backgroundColor: getStatusColor(),
            color: !isUnlimited && remaining === 0 ? '#fff' : undefined
          }}
        >
          {!isUnlimited && remaining === 0 ? (
            <ExclamationCircleOutlined 
              style={{ 
                color: '#ff4d4f',
                fontSize: '16px'
              }} 
            />
          ) : (
            <ThunderboltOutlined 
              style={{ 
                color: canUse ? theme.textPrimary : theme.textSecondary,
                fontSize: '16px'
              }} 
            />
          )}
        </Badge>
      </Tooltip>
    );
  }

  // 默认尺寸显示
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <Tooltip title={getStatusText()}>
        <div 
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '6px',
            cursor: 'pointer',
            padding: '4px 8px',
            borderRadius: '6px',
            background: theme.surfaceBackground,
            border: `1px solid ${theme.surfaceBorder}`,
            transition: 'all 0.2s ease'
          }}
          onClick={showUsageModal}
        >
          <ThunderboltOutlined 
            style={{ 
              color: canUse ? theme.accentPrimary : theme.textSecondary,
              fontSize: '14px'
            }} 
          />
          <span style={{ 
            color: theme.textPrimary,
            fontSize: '12px',
            fontWeight: '500'
          }}>
            {displayRemaining}/{displayLimit}
          </span>
          {!canUse && (
            <ExclamationCircleOutlined 
              style={{ 
                color: '#ff4d4f',
                fontSize: '12px'
              }} 
            />
          )}
        </div>
      </Tooltip>

      {showDetails && (
        <div style={{ minWidth: '120px' }}>
          <Progress
            percent={percentage}
            size="small"
            strokeColor={getStatusColor()}
            trailColor={theme.surfaceBorder}
            showInfo={false}
          />
        </div>
      )}

      {/* 详细使用情况模态框 */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ThunderboltOutlined style={{ color: theme.accentPrimary }} />
            <span style={{ color: theme.textPrimary }}>
              {i18n.language === 'zh-CN' ? 'AI操作使用情况' : 'AI Operations Usage'}
            </span>
          </div>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button 
            key="refresh" 
            icon={<ReloadOutlined />} 
            onClick={handleRefresh}
            loading={loading}
          >
            {i18n.language === 'zh-CN' ? '刷新' : 'Refresh'}
          </Button>,
          <Button 
            key="close" 
            type="primary" 
            onClick={() => setModalVisible(false)}
          >
            {i18n.language === 'zh-CN' ? '关闭' : 'Close'}
          </Button>
        ]}
        styles={{
          body: {
            background: theme.surfaceBackground,
            color: theme.textPrimary
          }
        }}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <div style={{ marginBottom: '24px' }}>
            <Progress
              type="circle"
              percent={percentage}
              strokeColor={getStatusColor()}
              trailColor={theme.surfaceBorder}
              size={120}
              format={() => (
                <div style={{ color: theme.textPrimary }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                    {displayRemaining}
                  </div>
                  <div style={{ fontSize: '12px', color: theme.textSecondary }}>
                    {i18n.language === 'zh-CN' ? '剩余次数' : 'Remaining'}
                  </div>
                </div>
              )}
            />
          </div>

          <div style={{ 
            background: theme.surfaceBackground,
            border: `1px solid ${theme.surfaceBorder}`,
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '16px'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              marginBottom: '8px'
            }}>
              <span style={{ color: theme.textSecondary }}>
                {i18n.language === 'zh-CN' ? '已使用' : 'Used'}
              </span>
                <span style={{ color: theme.textPrimary, fontWeight: '500' }}>
                {used}/{displayLimit}
              </span>
            </div>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              marginBottom: '8px'
            }}>
              <span style={{ color: theme.textSecondary }}>
                {i18n.language === 'zh-CN' ? '剩余' : 'Remaining'}
              </span>
              <span style={{ 
                color: getStatusColor(), 
                fontWeight: '500' 
              }}>
                {displayRemaining}
              </span>
            </div>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between'
            }}>
              <span style={{ color: theme.textSecondary }}>
                {i18n.language === 'zh-CN' ? '每日限制' : 'Daily Limit'}
              </span>
              <span style={{ color: theme.textPrimary, fontWeight: '500' }}>
                {displayLimit}
              </span>
            </div>
          </div>

          {!canUse && !isUnlimited && (
            <div style={{ 
              background: '#fff2f0',
              border: '1px solid #ffccc7',
              borderRadius: '6px',
              padding: '12px',
              color: '#ff4d4f'
            }}>
              <ExclamationCircleOutlined style={{ marginRight: '8px' }} />
              {i18n.language === 'zh-CN' 
                ? '今日AI操作次数已用完，请明天再试' 
                : 'Daily AI operations limit reached. Please try again tomorrow.'
              }
            </div>
          )}

          <div style={{ 
            marginTop: '16px',
            fontSize: '12px',
            color: theme.textSecondary
          }}>
            {i18n.language === 'zh-CN' 
              ? '使用次数将在每天午夜重置' 
              : 'Usage count resets daily at midnight'
            }
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default UsageIndicator;
